import moment from 'moment';
import t from 'react-translate';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AngularContext } from 'react-app';
import { DATE_FORMAT, FirstSeenKey, PanelStateKey } from 'athena/components/constants';
import CollapsiblePanel from 'athena/components/collapsible-panel';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import {
  CompletionStatus,
  MentoringProgramEnrollment,
  States,
  StepKey,
} from 'redux/schemas/models/mentoring-program-enrollments';
import { MentorshipProgramSession } from 'redux/schemas/models/mentoring-program-sessions';
import { getMentoringProgramEnrollment } from 'redux/selectors/mentoring-program-enrollments';
import StepItem from './step-item';
import { Step } from './types';
import CompleteProfileButton from './profile-completion/complete-profile-button';
import { config } from '@config/pendo.config.json';
import SlideTransition, { Slide } from 'athena/components/slide-transition';
import StarsConfetti from 'athena/components/stars-confetti';
import { css } from '@emotion/react';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import FindAMentorButton from './find-a-mentor/find-a-mentor-button';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { useAppDispatch } from 'redux/store';
import { getMentorsQuestionAnswers } from 'redux/actions/mentoring-program-participant-driven';
import { getStatus } from './utils';
import { getMentoringProgramRequestsByStatus } from 'redux/selectors/mentorship-program-connections';
import { SingleMentorRequestStatus } from 'redux/schemas/models/mentor-request';

const DAYS_BEFORE_MATCH = 5;

const isWaitingFirstSessionCompleted = (states: States, isSessionCreated: boolean) => (
  states[StepKey.WAITING_FIRST_SESSION]?.status === CompletionStatus.COMPLETED && isSessionCreated
);

const getProfileCompletionTitle = (states, translationBase) => {
  // Show review your profile title if profile_completion is completed
  const title =
    states[StepKey.PROFILE_COMPLETION]?.status === CompletionStatus.COMPLETED
      ? translationBase.REVIEW_YOUR_PROFILE.TITLE()
      : translationBase.COMPLETE_YOUR_PROFILE.TITLE();
  return title;
};

const getProfileCompletionContent = (states, completionDate, translationBase) => {
  if (states[StepKey.PROFILE_COMPLETION]?.status === CompletionStatus.IN_PROGRESS
      && states[StepKey.PROFILE_COMPLETION]?.previousStatus === CompletionStatus.COMPLETED) {
    return translationBase.COMPLETE_YOUR_PROFILE.NEW_INFO_NEEDED();
  }
  if (completionDate) {
    return translationBase.COMPLETE_YOUR_PROFILE.COMPLETE_BY_DATE(completionDate);
  }
}

type StepListProps = {
  showPlanSessionFlyout: (
    session?: MentorshipProgramSession,
    editMode?: boolean,
    createMeetingPlan?: boolean,
  ) => void,
};

const SLIDE_OUT_DELAY = 5000;

const styles = css`
  gap: ${halfSpacing}px;
`;

const StepList = ({ showPlanSessionFlyout }: StepListProps) => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentoringProgramId,
    matchAnnouncementDate,
    mentorshipProgramEnrollment: enrollmentId,
    matchingType
  } = mentoringProgram || {};
  const mentorshipProgramEnrollment: MentoringProgramEnrollment = useSelector(
    (state) => getMentoringProgramEnrollment(state, enrollmentId)
  );
  const isAutomatedMatching = matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING;
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;
  const { isMentee, isMentor } = useMentoringProgramRole();

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const connectionId = parseInt($state.params.connectionId, 10);
  const mentoringProgramAliases = useSelector((state) =>
    getMentoringProgramAliases(state, mentoringProgramId)
  );
  const pendingMentoringRequests = useSelector(
    getMentoringProgramRequestsByStatus(SingleMentorRequestStatus.PENDING, enrollmentId)
  );
  const { states: enrollmentStates, assignedConnections } = mentorshipProgramEnrollment || {};

  const connection =
    assignedConnections?.find(
      (connec) => connec.connectionId === connectionId
    ) ?? assignedConnections?.[0];
  const { isSessionCreated, states: connectionStates } = connection || {};
  const states = useMemo(
      () => ({ ...enrollmentStates, ...connectionStates }),
      [enrollmentStates, connectionStates]
    );

  const completionDate = matchAnnouncementDate
    ? moment(matchAnnouncementDate)
        .subtract(DAYS_BEFORE_MATCH, 'days')
        .format(DATE_FORMAT)
    : null;

  const formattedMatchAnnouncementDate = matchAnnouncementDate
    ? moment(matchAnnouncementDate).format(DATE_FORMAT)
    : null;
  const isMatchUpcoming = moment(matchAnnouncementDate).isAfter(moment());
  const isMatchPast = moment().isAfter(moment(matchAnnouncementDate));
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.LIST;
  const upcomingMatchMessage =
    translationBase.WAIT_TO_BE_CONNECTED.UPCOMING_MATCH_DATE(
      formattedMatchAnnouncementDate
    );
  const passedMatchMessage = assignedConnections?.length
    ? translationBase.WAIT_TO_BE_CONNECTED.PASSED_MATCH_DATE_WITH_CONNECTION(
        formattedMatchAnnouncementDate
      )
    : translationBase.WAIT_TO_BE_CONNECTED.PASSED_MATCH_DATE_WITHOUT_CONNECTION();
  const getContentBasedOnMatchAnnouncementDate = () =>
    isMatchUpcoming ? upcomingMatchMessage : passedMatchMessage;

  const buttonLabel = translationBase.PLAN_FIRST_SESSION.ADD_SESSION();

  const findMentorSeenLocalStorageKey = `${mentoringProgramId}-${enrollmentId}-${FirstSeenKey.FIND_A_MENTOR_SEEN}`;
  const findAMentorStepSeen = localStorage.getItem(findMentorSeenLocalStorageKey) === 'true';

  const profileCompletionStep = {
    title: getProfileCompletionTitle(states, translationBase),
    content: getProfileCompletionContent(
      states,
      completionDate,
      translationBase
    ),
    showBadge: true,
    ctaButton: <CompleteProfileButton states={states} />,
  };
  const waitingConnectionStep = {
    title: translationBase.WAIT_TO_BE_CONNECTED.TITLE(),
    content: matchAnnouncementDate
      ? getContentBasedOnMatchAnnouncementDate()
      : null,
  };

  const profileCompletionStatus = getStatus(states, StepKey.PROFILE_COMPLETION);
  const showFindAMentorButton =
    !assignedConnections.length && (profileCompletionStatus === CompletionStatus.COMPLETED || profileCompletionStatus === CompletionStatus.IN_PROGRESS)
    || findAMentorStepSeen;
  const findAMentorStep = {
    title: translationBase.FIND_A_MENTOR.TITLE(mentoringProgramAliases),
    content: translationBase.FIND_A_MENTOR.CONTENT(),
    ctaButton: showFindAMentorButton ? (
      <FindAMentorButton
        localStorageKey={findMentorSeenLocalStorageKey}
        label={translationBase.FIND_A_MENTOR.BROWSE_MENTORS(
          mentoringProgramAliases
        )}
      />
    ) : null,
  };

  const reviewMenteeRequestsStep = {
    title: translationBase.REVIEW_MENTEE_REQUESTS.TITLE(mentoringProgramAliases),
    content: translationBase.REVIEW_MENTEE_REQUESTS.CONTENT(mentoringProgramAliases),
  };

  const waitingFirstSessionStep = {
    title: translationBase.PLAN_FIRST_SESSION.TITLE(),
    content: translationBase.PLAN_FIRST_SESSION.CONTENT(),
    ctaButton:
      (isMatchPast || isParticipantDrivenMatching) &&
      assignedConnections.length &&
      !isWaitingFirstSessionCompleted(states, isSessionCreated) ? (
        <Button
          size='lg'
          onClick={() => showPlanSessionFlyout(null, true)}
          data-qa={
            config.pendo.athena.mentorshipProgram.participantHome.steps
              .addFirstSession
          }
        >
          {buttonLabel}
        </Button>
      ) : null,
  };

  const isFindAMentorStepShown = isParticipantDrivenMatching && isMentee;
  const isReviewMenteeRequestsStepShown = isParticipantDrivenMatching && isMentor;

  const stepsList: { [key in StepKey]?: Step } = {
    [StepKey.PROFILE_COMPLETION]: profileCompletionStep,
    ...isAutomatedMatching && { [StepKey.WAITING_CONNECTION]: waitingConnectionStep },
    ...isFindAMentorStepShown && { [StepKey.FIND_A_MENTOR]: findAMentorStep },
    ...isReviewMenteeRequestsStepShown && { [StepKey.REVIEW_MENTEE_REQUESTS]: reviewMenteeRequestsStep },
    [StepKey.WAITING_FIRST_SESSION]: waitingFirstSessionStep,
  };

  useEffect(() => {
    if (isFindAMentorStepShown && mentoringProgramId) {
      // Preload Browse Mentors Filters when Find a Mentor step is visible
      dispatch(getMentorsQuestionAnswers({ programId: mentoringProgramId }));
    }
  }, [isFindAMentorStepShown])

  const areAllStepsCompleted = Object.keys(stepsList).every(
    (key) =>
      getStatus(states, key, isSessionCreated) === CompletionStatus.COMPLETED
  );
  const [showConfetti, setShowConfetti] = useState(false);

  const onSeen = () => {
    if (areAllStepsCompleted) {
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);
    }
  };

  const panelTitle = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.TITLE();

  const hasAMentor = isFindAMentorStepShown && assignedConnections.length > 0;

  const hasPendingMentorRequests = pendingMentoringRequests?.length > 0;

  const hasAMentee = isReviewMenteeRequestsStepShown && assignedConnections.length > 0;

  return (
    <SlideTransition
      type={Slide.OUT}
      showTransition={areAllStepsCompleted}
      delay={SLIDE_OUT_DELAY}
      onSeen={onSeen}
    >
      <CollapsiblePanel
        title={panelTitle}
        panelState={{
          persistState: true,
          localStorageKey: PanelStateKey.STEPS_LIST_STATE,
        }}
        dataQa={
          config.pendo.athena.mentorshipProgram.participantHome.steps.list
        }
        ariaLabel={panelTitle}
        className={showConfetti ? 'show-gradient' : ''}
      >
        <div css={styles} className='d-flex flex-column mt-4'>
          {Object.entries(stepsList).map(
            ([key, { title, content, showBadge, ctaButton }], index) => {
              let component = (
                <StepItem
                  key={key}
                  status={getStatus(
                    states,
                    key,
                    isSessionCreated,
                    hasAMentor,
                    hasPendingMentorRequests,
                    hasAMentee,
                  )}
                  title={title}
                  content={content}
                  showBadge={showBadge}
                  ctaButton={ctaButton}
                />
              );
              if (index === Object.entries(stepsList).length - 1)
                return (
                  <StarsConfetti key={key} showLeft={showConfetti}>
                    {component}
                  </StarsConfetti>
                );
              return component;
            }
          )}
        </div>
      </CollapsiblePanel>
    </SlideTransition>
  );
};

export default StepList;
